import { useEffect, useRef, useState } from 'react'
import { FullscreenLoaderArt } from './FullscreenLoaderArt'
import { getNlsString } from '../../nls/nlsRuntime'
import { useShortLoad } from './useShortLoad'
import { Button } from './Button'

interface FullscreenLoaderProps {
  /** Optional line shown under the loader (e.g. "Setting up your workspace"). */
  overlayText?: string
  /**
   * While true, the loader keeps animating. When it flips to false the
   * short-load rule runs before calling `onFinish` and unmounting.
   */
  waitForReady: boolean
  onFinish: () => void
  /**
   * Optional error passed by caller (e.g. workspaceLoadingError).
   * When truthy, immediately shows the stall recovery card.
   */
  error?: string | Error | null
}

/**
 * Full-viewport loader. Renders FullscreenLoaderArt against a solid backdrop.
 * When loading stalls (20s timeout, 5s offline, or error passed), displays a
 * stall card with a Retry action.
 */
export const FullscreenLoader = ({
  overlayText,
  waitForReady,
  onFinish,
  error,
}: FullscreenLoaderProps) => {
  const visible = useShortLoad(waitForReady)
  const finishedRef = useRef(false)
  const [timedOut, setTimedOut] = useState(false)
  const [isOfflineStalled, setIsOfflineStalled] = useState(false)

  const isStalled = Boolean(error) || timedOut || isOfflineStalled

  useEffect(() => {
    if (!waitForReady || isStalled) return
    const timer = window.setTimeout(() => {
      setTimedOut(true)
    }, 20000)
    return () => window.clearTimeout(timer)
  }, [waitForReady, isStalled])

  useEffect(() => {
    if (isStalled) return

    let offlineTimer: number | null = null

    const checkOffline = () => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        if (offlineTimer === null) {
          offlineTimer = window.setTimeout(() => {
            setIsOfflineStalled(true)
          }, 5000)
        }
      } else {
        if (offlineTimer !== null) {
          window.clearTimeout(offlineTimer)
          offlineTimer = null
        }
      }
    }

    checkOffline()
    window.addEventListener('offline', checkOffline)
    window.addEventListener('online', checkOffline)

    return () => {
      if (offlineTimer !== null) {
        window.clearTimeout(offlineTimer)
      }
      window.removeEventListener('offline', checkOffline)
      window.removeEventListener('online', checkOffline)
    }
  }, [isStalled])

  useEffect(() => {
    if (!waitForReady && !visible && !finishedRef.current && !isStalled) {
      finishedRef.current = true
      onFinish()
    }
  }, [waitForReady, visible, onFinish, isStalled])

  const handleRetry = () => {
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload()
    }
  }

  return (
    <div
      className="appFullscreenLoader"
      role="status"
      aria-live="polite"
      aria-label={overlayText || getNlsString('NLSCommonStatusLoading')}
    >
      {isStalled ? (
        <section
          className="appFullscreenLoader__stallCard"
          role="alert"
        >
          <h2 className="appFullscreenLoader__stallTitle">
            {getNlsString('NLSLoaderStallTitle')}
          </h2>
          <p className="appFullscreenLoader__stallBody">
            {getNlsString('NLSLoaderStallDescription')}
          </p>
          <div className="appFullscreenLoader__stallActions">
            <Button
              variant="primary"
              size="md"
              onClick={handleRetry}
            >
              {getNlsString('NLSLoaderStallRetry')}
            </Button>
          </div>
        </section>
      ) : (
        <div className="appFullscreenLoader__inner">
          {visible && <FullscreenLoaderArt />}
          {overlayText && <p className="appFullscreenLoader__text">{overlayText}</p>}
        </div>
      )}
    </div>
  )
}
